// client/src/components/pages/DriverPWA/offlineQueue.ts (ST-00228, audit TRK-20)
//
// Uploading the Driver PWA's offline position queue. Storage-agnostic: OfflineSync.tsx
// plugs its IndexedDB object store in through LocationQueueStore, the tests an in-memory
// one.
//
// Rules:
//   - only the fixes actually sent are removed; anything queued while an upload is in
//     flight stays for the next sync (the old code cleared the whole store);
//   - overlapping sync() calls share the one in-flight run (no double upload);
//   - at most MAX_SYNC_BATCH fixes per request (the server refuses more than 1,000);
//   - fixes the server rejects as invalid (a 200 with `rejected`, or a 400 "No valid
//     locations in batch") are dropped: they can never succeed;
//   - any other failure (no session 401, not allowed 403, server or network error) keeps
//     everything queued and stops this run.

import type { DriverLocationMessage } from '../../../types/clientPages/driverPWA';

/** Fixes per upload request (the server accepts up to 1,000). */
export const MAX_SYNC_BATCH = 500;

/** A queued fix as IndexedDB holds it (autoincrement key `id`). */
export interface QueuedLocation extends DriverLocationMessage {
  id: number;
  source?: string;
  queuedAt?: string;
}

/** POST /api/vehicle-locations/batch body row. */
export interface BatchLocation extends DriverLocationMessage {
  source: string;
}

/** POST /api/vehicle-locations/batch 200 body (vehicleLocationService.batchUpload). */
export interface BatchSyncResponseBody {
  success?: boolean;
  count?: number;
  rejected?: { index: number; error: string }[];
}

/** Sends one batch; resolves with the axios-style response, rejects on HTTP errors. */
export type UploadBatch = (locations: BatchLocation[]) => Promise<{ data?: BatchSyncResponseBody }>;

/** The queue storage the syncer works on. */
export interface LocationQueueStore {
  getAll(): Promise<QueuedLocation[]>;
  deleteKeys(keys: number[]): Promise<void>;
  count(): Promise<number>;
}

export interface QueueSyncResult {
  /** Fixes the server stored. */
  uploaded: number;
  /** Fixes dropped because the server rejected them as invalid. */
  dropped: number;
  /** Fixes still queued afterwards. */
  remaining: number;
}

interface HttpFailure {
  response?: { status?: number; data?: { error?: string } };
}

/** The server refused the whole batch as invalid: retrying can never succeed. */
const isPermanentRejection = (err: unknown): boolean => {
  const res = (err as HttpFailure)?.response;
  return res?.status === 400 && res.data?.error === 'No valid locations in batch';
};

const toBatchLocation = (item: QueuedLocation): BatchLocation => ({
  regNo: item.regNo,
  lat: item.lat,
  lng: item.lng,
  speed: item.speed,
  heading: item.heading,
  accuracy: item.accuracy,
  altitude: item.altitude,
  source: item.source || 'PWA',
  timestamp: item.timestamp,
});

async function runSync(store: LocationQueueStore, upload: UploadBatch): Promise<QueueSyncResult> {
  const items = (await store.getAll()).slice().sort((a, b) => a.id - b.id);
  let uploaded = 0;
  let dropped = 0;
  for (let i = 0; i < items.length; i += MAX_SYNC_BATCH) {
    const chunk = items.slice(i, i + MAX_SYNC_BATCH);
    const keys = chunk.map((item) => item.id);
    try {
      const res = await upload(chunk.map(toBatchLocation));
      const rejected = res?.data?.rejected?.length ?? 0;
      const accepted = res?.data?.count ?? chunk.length - rejected;
      uploaded += accepted;
      dropped += chunk.length - accepted;
      if (chunk.length - accepted > 0) {
        console.warn(
          `[PWA] ${chunk.length - accepted} queued position(s) rejected by the server as invalid and dropped`,
          res?.data?.rejected?.slice(0, 5)
        );
      }
      await store.deleteKeys(keys);
    } catch (err) {
      if (isPermanentRejection(err)) {
        console.warn(`[PWA] ${chunk.length} queued position(s) rejected as invalid and dropped`);
        dropped += chunk.length;
        await store.deleteKeys(keys);
        continue;
      }
      console.error('[PWA] Offline queue upload failed; positions kept for the next sync:', err);
      break;
    }
  }
  return { uploaded, dropped, remaining: await store.count() };
}

/** A sync() that never runs twice at once: overlapping calls share the in-flight run. */
export function createQueueSyncer(
  store: LocationQueueStore,
  upload: UploadBatch
): { sync: () => Promise<QueueSyncResult> } {
  let inFlight: Promise<QueueSyncResult> | null = null;
  return {
    sync() {
      if (!inFlight) {
        inFlight = runSync(store, upload).finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    },
  };
}
